/**
 * Hook useAdminPlans - Admin Plan management API.
 * 
 * Provides CRUD operations for billing plans with:
 * - Cache invalidation on mutations
 * - Optimistic updates for toggle actions
 * - Error handling with i18n-ready messages
 * 
 * @security Admin-only endpoints (requires admin role)
 * @performance Request deduplication + stale-while-revalidate
 */
import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import { invalidateBillingCache } from './useBilling'

// =============================================================================
// Types (Admin-specific, extends base Plan)
// =============================================================================

export interface AdminPlanLimits {
  max_agents: number
  max_conversations_per_month: number
  max_knowledge_items: number
  max_api_calls_per_month: number
  max_file_size_mb: number
  max_storage_mb: number
  max_crawled_pages: number
}

export interface AdminPlan {
  id: number
  public_id: string
  name: string
  slug: string
  description: string | null
  price_monthly: number
  price_yearly: number | null
  currency: string
  features: string[]
  max_agents: number
  max_conversations_per_month: number
  max_messages_per_month: number
  max_knowledge_items: number
  max_api_calls_per_month: number
  max_file_size_mb: number
  max_storage_mb: number
  max_crawled_pages: number
  // Phase 1: Session Capping
  session_max_messages: number | null
  session_timeout_minutes: number | null
  session_max_duration_hours: number | null
  // Phase 1: Rate Limiting
  rate_limit_requests_per_minute: number | null
  rate_limit_requests_per_hour: number | null
  rate_limit_requests_per_day: number | null
  is_active: boolean
  is_featured: boolean
  is_hidden: boolean
  trial_days: number
  sort_order: number
  stripe_product_id: string | null
  stripe_price_id_monthly: string | null
  stripe_price_id_yearly: string | null
  created_at: string
  updated_at: string
  active_subscriptions: number
}

export interface PlanCreateData {
  name: string
  slug: string
  description?: string
  price_monthly: number
  price_yearly?: number
  currency?: string
  max_agents?: number
  max_conversations_per_month?: number
  max_messages_per_month?: number
  max_knowledge_items?: number
  max_api_calls_per_month?: number
  max_file_size_mb?: number
  max_storage_mb?: number
  max_crawled_pages?: number
  // Phase 1: Session Capping
  session_max_messages?: number | null
  session_timeout_minutes?: number | null
  session_max_duration_hours?: number | null
  // Phase 1: Rate Limiting
  rate_limit_requests_per_minute?: number | null
  rate_limit_requests_per_hour?: number | null
  rate_limit_requests_per_day?: number | null
  trial_days?: number
  is_featured?: boolean
  is_hidden?: boolean
  sort_order?: number
}

export interface PlanUpdateData {
  name?: string
  description?: string
  price_monthly?: number
  price_yearly?: number
  features?: string[]
  max_agents?: number
  max_conversations_per_month?: number
  max_messages_per_month?: number
  max_knowledge_items?: number
  max_api_calls_per_month?: number
  max_file_size_mb?: number
  max_storage_mb?: number
  max_crawled_pages?: number
  // Phase 1: Session Capping
  session_max_messages?: number | null
  session_timeout_minutes?: number | null
  session_max_duration_hours?: number | null
  // Phase 1: Rate Limiting
  rate_limit_requests_per_minute?: number | null
  rate_limit_requests_per_hour?: number | null
  rate_limit_requests_per_day?: number | null
  trial_days?: number
  is_featured?: boolean
  is_hidden?: boolean
  is_active?: boolean
  sort_order?: number
}

export interface PlanAuditEntry {
  id: number
  action: string
  changed_by: string | null
  changes: Record<string, unknown>
  ip_address: string | null
  created_at: string
}

export interface StripeDLQEntry {
  id: number
  plan_id: number
  operation: string
  error_message: string
  retry_count: number
  created_at: string
  last_retry_at: string | null
}

export interface CircuitStatus {
  state: 'closed' | 'open' | 'half_open'
  failure_count: number
  failure_threshold: number
  last_failure: string | null
  recovery_timeout_seconds: number
}

// Feature whitelist (matching backend)
export const ALLOWED_FEATURES = [
  'basic_chat',
  'rag',
  'mcp_tools',
  'workflows',
  'widget_embed',
  'whatsapp',
  'api_access',
  'remove_branding',
  'custom_branding',
  'priority_support',
  'analytics',
  'advanced_analytics',
] as const

export type FeatureName = typeof ALLOWED_FEATURES[number]

// Reserved slugs (matching backend)
export const RESERVED_SLUGS = [
  'admin', 'api', 'billing', 'checkout', 'free', 'internal', 'system'
] as const

// =============================================================================
// Hook: useAdminPlans
// =============================================================================

export function useAdminPlans() {
  const [plans, setPlans] = useState<AdminPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch all plans
  const fetchPlans = useCallback(async (includeInactive = true) => {
    try {
      setLoading(true)
      setError(null)
      const res = await api.get<AdminPlan[]>('/admin/plans', {
        params: { include_inactive: includeInactive }
      })
      setPlans(res.data)
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur chargement plans'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPlans()
  }, [fetchPlans])

  // Create plan
  const createPlan = useCallback(async (data: PlanCreateData, syncStripe = true): Promise<AdminPlan | null> => {
    try {
      const res = await api.post<AdminPlan>('/admin/plans', data, {
        params: { sync_stripe: syncStripe }
      })
      setPlans(prev => [...prev, res.data])
      invalidateBillingCache()
      return res.data
    } catch (err: any) {
      throw new Error(err?.response?.data?.detail || 'Erreur création plan')
    }
  }, [])

  // Update plan
  const updatePlan = useCallback(async (planId: number, data: PlanUpdateData): Promise<AdminPlan | null> => {
    try {
      const res = await api.patch<AdminPlan>(`/admin/plans/${planId}`, data)
      setPlans(prev => prev.map(p => p.id === planId ? res.data : p))
      invalidateBillingCache()
      return res.data
    } catch (err: any) {
      throw new Error(err?.response?.data?.detail || 'Erreur mise à jour plan')
    }
  }, [])

  // Toggle active status
  const toggleActive = useCallback(async (plan: AdminPlan): Promise<void> => {
    // Optimistic update
    setPlans(prev => prev.map(p => 
      p.id === plan.id ? { ...p, is_active: !p.is_active } : p
    ))
    
    try {
      if (plan.is_active) {
        await api.delete(`/admin/plans/${plan.id}`)
      } else {
        await api.post(`/admin/plans/${plan.id}/activate`)
      }
      invalidateBillingCache()
    } catch (err: any) {
      // Rollback
      setPlans(prev => prev.map(p => 
        p.id === plan.id ? { ...p, is_active: plan.is_active } : p
      ))
      throw new Error(err?.response?.data?.detail || 'Erreur toggle statut')
    }
  }, [])

  // Sync to Stripe
  const syncStripe = useCallback(async (planId: number, force = false): Promise<void> => {
    try {
      await api.post(`/admin/plans/${planId}/sync-stripe`, null, {
        params: { force }
      })
      await fetchPlans()
    } catch (err: any) {
      throw new Error(err?.response?.data?.detail || 'Erreur sync Stripe')
    }
  }, [fetchPlans])

  // Delete plan permanently
  const deletePlan = useCallback(async (planId: number): Promise<void> => {
    try {
      await api.delete(`/admin/plans/${planId}/permanent`)
      setPlans(prev => prev.filter(p => p.id !== planId))
      invalidateBillingCache()
    } catch (err: any) {
      throw new Error(err?.response?.data?.detail || 'Erreur suppression plan')
    }
  }, [])

  // Get audit log
  const getAuditLog = useCallback(async (planId: number, limit = 50): Promise<PlanAuditEntry[]> => {
    try {
      const res = await api.get<PlanAuditEntry[]>(`/admin/plans/${planId}/audit`, {
        params: { limit }
      })
      return res.data
    } catch (err: any) {
      throw new Error(err?.response?.data?.detail || 'Erreur chargement audit')
    }
  }, [])

  return {
    plans,
    loading,
    error,
    refetch: fetchPlans,
    createPlan,
    updatePlan,
    toggleActive,
    syncStripe,
    deletePlan,
    getAuditLog,
  }
}

// =============================================================================
// Hook: useStripeStatus
// =============================================================================

export function useStripeStatus() {
  const [circuitStatus, setCircuitStatus] = useState<CircuitStatus | null>(null)
  const [dlqEntries, setDlqEntries] = useState<StripeDLQEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [stripeAvailable, setStripeAvailable] = useState<boolean | null>(null)

  const fetchStatus = useCallback(async () => {
    // Skip if we already know Stripe is unavailable
    if (stripeAvailable === false) return

    try {
      setLoading(true)
      const [circuitRes, dlqRes] = await Promise.all([
        api.get<CircuitStatus>('/admin/plans/stripe/circuit-status').catch((err) => {
          // Mark Stripe as unavailable on 503
          if (err?.response?.status === 503) {
            setStripeAvailable(false)
          }
          return null
        }),
        api.get<StripeDLQEntry[]>('/admin/plans/stripe/dlq', { params: { limit: 20 } }).catch((err) => {
          if (err?.response?.status === 503) {
            setStripeAvailable(false)
          }
          return null
        }),
      ])
      if (circuitRes) {
        setCircuitStatus(circuitRes.data)
        setStripeAvailable(true)
      }
      if (dlqRes) {
        setDlqEntries(dlqRes.data)
        setStripeAvailable(true)
      }
    } catch {
      // Silently fail - Stripe not configured or unavailable
      setStripeAvailable(false)
    } finally {
      setLoading(false)
    }
  }, [stripeAvailable])

  const retryDLQ = useCallback(async (dlqId: number): Promise<boolean> => {
    try {
      await api.post(`/admin/plans/stripe/dlq/${dlqId}/retry`)
      await fetchStatus()
      return true
    } catch {
      return false
    }
  }, [fetchStatus])

  // Don't auto-fetch on mount - Stripe may not be configured
  // User can manually refetch if needed
  // useEffect(() => {
  //   fetchStatus()
  // }, [fetchStatus])

  return {
    circuitStatus,
    dlqEntries,
    loading,
    refetch: fetchStatus,
    retryDLQ,
    stripeAvailable,
  }
}
